<template>
    <div class="space-y-6 pb-10">
        <h2 class="text-heading-md">Card Radio Examples</h2>

        <div class="grid grid-cols-2 gap-8">
            <div>
                <div class="mb-3">
                    <h3 class="mb-3 text-content-lg font-semibold">Basic Card Radios</h3>
                    <DsCardOptionList>
                        <DsCardRadio
                            v-model="planSelection"
                            helperText="Perfect for individuals - $9/month"
                            label="Basic plan"
                            name="plan"
                            value="basic"
                        />
                        <DsCardRadio
                            v-model="planSelection"
                            helperText="For teams and professionals - $29/month"
                            label="Pro plan"
                            name="plan"
                            value="pro"
                        />
                        <DsCardRadio
                            v-model="planSelection"
                            helperText="Custom solutions for large organizations"
                            label="Enterprise plan"
                            name="plan"
                            value="enterprise"
                        />
                    </DsCardOptionList>
                    <div class="mt-3 text-content-sm text-content-secondary">Selected plan: {{ planSelection }}</div>
                </div>

                <div class="mb-3">
                    <h3 class="mb-3 text-content-lg font-semibold">Card Radio with Error State</h3>
                    <DsCardOptionList>
                        <DsCardRadio
                            v-model="radioError"
                            :error="!radioError"
                            helperText="First available option"
                            label="Option A"
                            name="required"
                            value="a"
                        />
                        <DsCardRadio
                            v-model="radioError"
                            :error="!radioError ? 'Please select an option to continue' : false"
                            helperText="Second available option"
                            label="Option B"
                            name="required"
                            value="b"
                        />
                    </DsCardOptionList>
                </div>

                <div class="mb-3">
                    <h3 class="mb-3 text-content-lg font-semibold">Card Radio with Disabled State</h3>
                    <DsCardOptionList>
                        <DsCardRadio
                            v-model="radioError"
                            disabled
                            helperText="First available option"
                            label="Option A"
                            name="required"
                            value="a"
                        />
                        <DsCardRadio
                            v-model="radioError"
                            disabled
                            helperText="Second available option"
                            label="Option B"
                            name="required"
                            value="b"
                        />
                    </DsCardOptionList>
                </div>
            </div>

            <div>
                <h3 class="mb-3 text-content-lg font-semibold">Form control position</h3>
                <h4>Right side of the card</h4>
                <DsCardOptionList>
                    <DsCardRadio
                        v-model="themeSelection"
                        formControlPosition="right"
                        name="theme"
                        value="light"
                    >
                        <div class="flex items-center gap-4">
                            <div class="h-12 w-12 rounded-lg border border-ds-gray-200 bg-white"></div>
                            <div>
                                <h4 class="text-content-lg font-semibold">Light Theme</h4>
                                <p class="text-content-sm text-content-secondary">Clean and bright interface</p>
                            </div>
                        </div>
                    </DsCardRadio>

                    <DsCardRadio
                        v-model="themeSelection"
                        formControlPosition="right"
                        name="theme"
                        value="dark"
                    >
                        <div class="flex items-center gap-4">
                            <div class="h-12 w-12 rounded-lg bg-ds-gray-800"></div>
                            <div>
                                <h4 class="text-content-lg font-semibold">Dark Theme</h4>
                                <p class="text-content-sm text-content-secondary">Easy on the eyes in low light</p>
                            </div>
                        </div>
                    </DsCardRadio>
                </DsCardOptionList>

                <h4 class="mt-12">Left side of the card</h4>
                <DsCardOptionList>
                    <DsCardRadio
                        v-model="colorSelection"
                        formControlPosition="left"
                        name="colorTheme"
                        value="blue"
                    >
                        <div class="flex items-center gap-4">
                            <div>
                                <h4 class="text-content-lg font-semibold">Blue Theme</h4>
                            </div>
                            <div class="h-12 w-12 rounded-lg border border-ds-gray-200 bg-ds-blue-500"></div>
                        </div>
                    </DsCardRadio>

                    <DsCardRadio
                        v-model="colorSelection"
                        formControlPosition="left"
                        name="colorTheme"
                        value="red"
                    >
                        <div class="flex items-center gap-4">
                            <div>
                                <h4 class="text-content-lg font-semibold">Red Theme</h4>
                            </div>
                            <div class="h-12 w-12 rounded-lg bg-ds-red-500"></div>
                        </div>
                    </DsCardRadio>
                </DsCardOptionList>
            </div>
        </div>

        <div class="grid grid-cols-2 gap-8">
            <div>
                <h3 class="mb-3 text-content-lg font-semibold">With slotted content</h3>
                <small>(use the default slot when you need richer content than label/helperText props can express)</small>
                <DsCardOptionList>
                    <DsCardRadio
                        v-model="planSelection"
                        name="planSlot"
                        value="basic"
                    >
                        <div>
                            <h4 class="text-content-lg font-semibold">Basic Plan</h4>
                            <p class="text-content-sm text-content-secondary">Perfect for individuals - $9/month</p>
                        </div>
                    </DsCardRadio>
                    <DsCardRadio
                        v-model="planSelection"
                        name="planSlot"
                        value="pro"
                    >
                        <div>
                            <h4 class="text-content-lg font-semibold">Pro Plan</h4>
                            <p class="text-content-sm text-content-secondary">For teams and professionals - $29/month</p>
                        </div>
                    </DsCardRadio>
                </DsCardOptionList>
            </div>
        </div>

        <div class="grid grid-cols-2 gap-8">
            <div>
                <h3 class="mb-3 text-content-lg font-semibold">Compact Card Radios</h3>
                <small>(uses DsCardOptionList for spacing normalization and compact mode)</small>
                <DsCardOptionList spacing="overlap">
                    <DsCardRadio
                        v-model="sizeSelection"
                        label="Small"
                        name="size"
                        value="sm"
                    />
                    <DsCardRadio
                        v-model="sizeSelection"
                        label="Medium"
                        name="size"
                        value="md"
                    />
                    <DsCardRadio
                        v-model="sizeSelection"
                        label="Large"
                        name="size"
                        value="lg"
                    />
                    <DsCardRadio
                        v-model="sizeSelection"
                        label="X Large"
                        name="size"
                        value="xlg"
                    />
                </DsCardOptionList>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import DsCardOptionList from '@/components/design-system/form/input/DsCardOptionList.vue';
import DsCardRadio from '@ds/form/input/DsCardRadio.vue';
import { ref } from 'vue';

// Radio states
const planSelection = ref('pro');
const themeSelection = ref('light');
const colorSelection = ref('blue');
const sizeSelection = ref('');
const radioError = ref('');
</script>
